import axios from 'axios';
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import type { FetchInterceptorResponse } from 'fetch-intercept';
import { get, clone } from 'lodash';
import type { Store } from 'redux';
import type { Overwrite } from 'utility-types';
import { clearAuthenticated, dashboardApiServer, getAuthenticatedAccountId } from 'api/index';
import { refreshAccessToken } from 'api/modules/authentication';
import { UNAUTHORIZED_ERROR_CODES, HttpStatusCode } from '@commutifi-fe/constants';
import { deserializeRequestData } from 'api/utils';
import { logoutSuccess, unauthorizeUser } from 'store/modules/auth/actions';
import type { RootState } from 'store/index';
import config from 'config/index';

// Requests can opt out of the 401 refresh→retry→forceLocalLogout machinery
// below when a 401 from their endpoint is a DOMAIN answer rather than an
// authentication failure (e.g. older dashboards answer `/subdomains` with 401
// when nothing resolves for the subdomain). Module augmentation so any axios
// config in the app can carry the flag.
//
// TODO(workos-epic): transitional. api-dashboard now answers 200 for that case
// (epics/workos-auth 466c3985), so once that is deployed everywhere no endpoint
// sends a semantic 401 and this flag + the `skipAuthRefresh: true` on the
// subdomains fetch can be retired — only useSubdomain's null mapping is needed
// regardless.
declare module 'axios' {
  export interface AxiosRequestConfig {
    skipAuthRefresh?: boolean;
  }
}

/**
 * Extended FetchInterceptor type that supports async response handlers.
 * The fetch-intercept library supports this at runtime but its types don't reflect it.
 */
type FetchError = {
  request?: {
    url?: string;
  };
  url?: string;
};
type AsyncFetchInterceptor = {
  request?(url: string, config: Request): Promise<[string, Request]> | [string, Request];
  requestError?(error: unknown): Promise<unknown>;
  response?(response: FetchInterceptorResponse): FetchInterceptorResponse | Promise<Response>;
  responseError?(error: FetchError): Promise<unknown>;
};

// Lazy-loaded fetch-intercept to avoid initialization errors in test environments
// where global.fetch may not be available at module load time
let fetchInterceptModule: typeof import('fetch-intercept') | null = null;
const getFetchIntercept = async () => {
  if (!fetchInterceptModule) {
    const module = await import('fetch-intercept');
    // fetch-intercept uses CommonJS exports, so when using dynamic import()
    // the actual exports (register, clear) are on module.default
    fetchInterceptModule = module.default;
  }
  return fetchInterceptModule;
};
const hasToken = () => Boolean(getAuthenticatedAccountId());

/**
 * Token refresh state manager.
 * Handles concurrent 401s by queueing requests and only calling refresh once.
 * All queued requests are retried after refresh succeeds or rejected if refresh fails.
 *
 * Based on proven pattern: https://gist.github.com/Godofbrowser/bf118322301af3fc334437c683887c5f
 */
type FailedRequest = {
  resolve: () => void;
  reject: (error: unknown) => void;
};
const createTokenRefreshManager = () => {
  let isRefreshingToken = false;
  let failedQueue: FailedRequest[] = [];
  let hasDispatchedUnauthorize = false;
  const processQueue = (error: unknown) => {
    for (const request of failedQueue) {
      if (error) {
        request.reject(error);
      } else {
        request.resolve();
      }
    }
    failedQueue = [];
  };
  return {
    hasDispatchedUnauthorize: () => hasDispatchedUnauthorize,
    markUnauthorizeDispatched: () => {
      hasDispatchedUnauthorize = true;
    },
    reset: () => {
      isRefreshingToken = false;
      failedQueue = [];
      hasDispatchedUnauthorize = false;
    },
    isRefreshingToken: () => isRefreshingToken,
    queueRequest: (): Promise<void> => new Promise((resolve, reject) => {
      failedQueue.push({
        resolve,
        reject
      });
    }),
    startRefresh: async (): Promise<void> => {
      isRefreshingToken = true;
      try {
        await refreshAccessToken();
        processQueue(null);
      } catch (error) {
        processQueue(error);
        throw error;
      } finally {
        isRefreshingToken = false;
      }
    }
  };
};
const tokenRefreshManager = createTokenRefreshManager();

/**
 * Reset the token refresh manager. Called after successful login or when starting fresh.
 */
export const resetTokenRefreshManager = () => {
  tokenRefreshManager.reset();
};

/**
 * Check if a URL belongs to our backend API.
 * Only force logout on CORS/network errors from our own servers.
 */
const isCommutifiApiUrl = (url: string | undefined): boolean => {
  if (!url) return false;
  try {
    const requestUrl = new URL(url, window.location.origin);
    const apiUrl = new URL(config.dashboardApiUrl);

    // Check if the request is to our API (same host)
    return requestUrl.host === apiUrl.host;
  } catch {
    return false;
  }
};

/**
 * Detect if an error is a CORS/network error (no response from server).
 * CORS errors typically have no response object because the browser blocks the response.
 */
const isCorsOrNetworkError = (error: {
  response?: unknown;
  request?: unknown;
}): boolean => {
  // CORS errors have no response (blocked by browser)
  // Network errors also have no response
  return !error.response && error.request !== undefined;
};

/**
 * Force local logout without making API call.
 * Used when we can't communicate with the server (CORS/network issues) or refresh fails.
 *
 * Note: We don't send anything to Sentry here - the backend handles all auth error tracking.
 * The backend marks expected errors (expired tokens, wrong credentials) with sendToSentry: false
 * and unexpected errors (infrastructure issues) will be captured by backend Sentry.
 */
const forceLocalLogout = (store: Store<RootState>, error: unknown) => {
  clearAuthenticated();
  store.dispatch(logoutSuccess({
    error: UNAUTHORIZED_ERROR_CODES.ExpiredToken
  }));
  return Promise.reject(error);
};

// Keep track of interceptor ID to avoid duplicate registration
let responseInterceptorId: number | null = null;
export default {
  setupInterceptors: async (store: Store<RootState>) => {
    // Remove any previously registered interceptor to avoid duplicates.
    // In production, setupInterceptors is called once at app init, so this rarely triggers.
    // In tests, each test calls setupInterceptors - without eject, interceptors would stack.
    // Also handles HMR during development where modules may re-execute.
    if (responseInterceptorId !== null) {
      dashboardApiServer.interceptors.response.eject(responseInterceptorId);
    }
    responseInterceptorId = dashboardApiServer.interceptors.response.use((response: AxiosResponse) => response, async (error: Overwrite<AxiosError, {
      config: AxiosRequestConfig & {
        isRetry?: boolean;
      };
    }>) => {
      if (axios.isCancel(clone(error))) {
        return Promise.reject(error);
      }
      const {
        config: originalRequest,
        response
      } = error;

      // Handle CORS/network errors for our API only - don't retry, just force local logout
      // This prevents infinite loops when our server is unreachable or CORS is misconfigured
      // We only logout for our own API errors, not third-party services (Mapbox, Stripe, etc.)
      if (isCorsOrNetworkError(error) && isCommutifiApiUrl(originalRequest?.url)) {
        if (hasToken() || store.getState().auth.loggedIn) {
          // User was logged in but we can't reach our server - force local logout
          return forceLocalLogout(store, error);
        }
        return Promise.reject(error);
      }

      // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- A
      switch (get(response, 'status')) {
        case HttpStatusCode.Unauthorized:
          // A request can declare that a 401 from its endpoint is a DOMAIN
          // answer, not an authentication failure — e.g. older dashboards answer
          // `/subdomains` with 401 when nothing resolves for it. For
          // those, the refresh→retry→forceLocalLogout machinery below must not
          // run: right after SSO hydration writes `account:id`, it would tear
          // down the session a semantic 401 says nothing about. The caller
          // handles the rejection itself.
          if (originalRequest.skipAuthRefresh) {
            return Promise.reject(error);
          }
          if (!hasToken() && !store.getState().auth.loggedIn) {
            // User is not logged in, let the error go through
            // because those errors are handled by protected routes
            // and we want the user to be able to access public routes
            // even if there are not authenticated
            return Promise.reject(error);
          }
          if (originalRequest.isRetry || originalRequest.url?.includes('/authentication/token/refresh')) {
            // Refresh failed or we already retried - force local logout without API call
            // This prevents infinite loops when logout endpoint also fails
            return forceLocalLogout(store, error);
          }

          // If the user is not authenticated we redirect him to the auth page so he can enter
          // his credentials to login
          // We just want to continue if the user is authenticated and his token expired
          if (!hasToken()) {
            // Only dispatch unauthorizeUser once to prevent multiple 401s from causing race conditions
            if (!tokenRefreshManager.hasDispatchedUnauthorize()) {
              tokenRefreshManager.markUnauthorizeDispatched();
              store.dispatch(unauthorizeUser({
                error: UNAUTHORIZED_ERROR_CODES.Unauthorized
              }));
            }
            return Promise.reject(error);
          }

          // If refresh is already in progress, queue this request to retry after refresh completes
          if (tokenRefreshManager.isRefreshingToken()) {
            return tokenRefreshManager.queueRequest().then(() => {
              originalRequest.isRetry = true;
              deserializeRequestData(originalRequest);
              return dashboardApiServer(originalRequest);
            });
          }

          // Start refresh and retry this request
          try {
            await tokenRefreshManager.startRefresh();
            originalRequest.isRetry = true;
            deserializeRequestData(originalRequest);
            // Return the retry request directly - the synchronous interceptor mode
            // in api/index.tsx ensures this return value is properly propagated.
            // Don't add .catch() here - let errors propagate to the caller like the queued path.
            // If retry returns 401, the interceptor catches it on second pass (isRetry -> forceLocalLogout).
            // Adding .catch() would force logout on non-auth failures (500s, network blips) which is wrong.
            return dashboardApiServer(originalRequest);
          } catch (refreshError) {
            // Refresh failed - force local logout without making another API call
            return forceLocalLogout(store, refreshError);
          }
        default:
          break;
      }
      return Promise.reject(error);
    });
    const cache = new Map<string, {
      config: Request;
      createdAt: number;
    }>();

    /**
     * Force local logout for fetch interceptor without making API call.
     * Used when we can't communicate with the server (CORS/network issues) or refresh fails.
     *
     * Note: We don't send anything to Sentry here - the backend handles all auth error tracking.
     */
    const doForceLocalLogout = () => {
      clearAuthenticated();
      store.dispatch(logoutSuccess({
        error: UNAUTHORIZED_ERROR_CODES.ExpiredToken
      }));
      throw new Error('Unauthorized User');
    };

    // Dynamically load fetch-intercept to avoid module load errors in test environments
    const {
      register
    } = await getFetchIntercept();
    const interceptor: AsyncFetchInterceptor = {
      request: (url, config) => {
        cache.forEach((entry, cacheUrl) => {
          // 30 sec TTL, making sure we can access them during an intercepted 401 but
          // also making sure we don't bust the memory over time
          if (Math.abs(entry.createdAt - Math.floor(Date.now() / 1000)) > 30) {
            cache.delete(cacheUrl);
          }
        });
        if (url && config && !cache.get(url)) {
          cache.set(url, {
            config,
            createdAt: Math.floor(Date.now() / 1000)
          });
        }
        return [url, config];
      },
      response: res => {
        const originUrl = res.request.url;
        const {
          request: originalRequest
        } = res;
        switch (get(res, 'status')) {
          case HttpStatusCode.Unauthorized:
            {
              if (!hasToken() && !store.getState().auth.loggedIn) {
                // User is not logged in, let the error go through
                // because those errors are handled by protected routes
                // and we want the user to be able to access public routes
                // even if there are not authenticated
                throw new Error('User is not logged In');
              }

              // Already retried or refresh endpoint failed - force local logout without API call
              // This prevents infinite loops when logout endpoint also fails
              if (originalRequest.headers.get('x-commutifi-retry') === 'true' || originalRequest.url.includes('/authentication/token/refresh')) {
                doForceLocalLogout();
                return res;
              }

              // If the user is not authenticated we redirect him to the auth page so he can enter
              // his credentials to login
              // We just want to continue if the user is authenticated and his token expired
              if (!hasToken()) {
                // Only dispatch unauthorizeUser once to prevent multiple 401s from causing race conditions
                if (!tokenRefreshManager.hasDispatchedUnauthorize()) {
                  tokenRefreshManager.markUnauthorizeDispatched();
                  store.dispatch(unauthorizeUser({
                    error: UNAUTHORIZED_ERROR_CODES.Unauthorized
                  }));
                }
                throw new Error('Unauthorized user');
              }
              const retryRequest = () => {
                if (!cache.get(originUrl)?.config) {
                  throw new Error('Missing original request config');
                }
                return fetch(originalRequest.url, {
                  ...cache.get(originUrl)?.config,
                  headers: {
                    ...originalRequest.headers,
                    'x-commutifi-retry': 'true'
                  }
                }).then(fetchRes => {
                  cache.delete(originUrl);
                  return fetchRes;
                });
              };

              // If refresh is already in progress, queue this request to retry after refresh completes
              if (tokenRefreshManager.isRefreshingToken()) {
                return tokenRefreshManager.queueRequest().then(() => retryRequest()).catch(() => {
                  doForceLocalLogout();
                  return res;
                });
              }

              // Start refresh and retry this request
              return tokenRefreshManager.startRefresh().then(() => retryRequest()).catch(() => {
                // Refresh failed - force local logout without making another API call
                doForceLocalLogout();
                return res;
              });
            }
          default:
            break;
        }
        return res;
      },
      responseError: error => {
        // Handle CORS/network errors in fetch - these have no response
        // Only force logout for errors from our own API, not third-party services
        // Note: fetch-intercept doesn't provide URL in responseError, so we check the error message
        // or rely on the fact that most third-party calls use their own fetch instances
        const errorUrl = error?.request?.url || error?.url;
        if ((hasToken() || store.getState().auth.loggedIn) && isCommutifiApiUrl(errorUrl)) {
          doForceLocalLogout();
        }
        return Promise.reject(error);
      }
    };
    // @ts-expect-error -- fetch-intercept types don't support async responses but the library does at runtime
    register(interceptor);
  }
};